import React, { useState } from "react";
import {
  ArrowPathIcon,
  BookmarkIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { BookmarkIcon as BookmarkIconSolid } from "@heroicons/react/24/solid";
import CourieButton from "@src/common/components/Button/Button";
import { Tooltip } from "@src/common/components";
import WarningConfirmationModal from "@src/common/components/Modal/WarningConfirmationModal";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@src/common/components/Popover";
import SaveViewModal from "./SaveViewModal";
import { useSavedViewsContext } from "../context/SavedViewsContext";
import { SavedView } from "@api/graphql/generated/generated-types";

interface SavedViewsDropdownProps {
  // Bookmark icon only, with the active view's name in the tooltip. For
  // narrow toolbars such as the Routes Map list header.
  iconOnly?: boolean;
}

function SavedViewsDropdown({ iconOnly = false }: SavedViewsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    savedView,
    savedViews,
    savedViewsLoading,
    isApplied,
    applySavedView,
    switchToView,
    clearAppliedView,
    saveCurrentView,
    updateSavedView,
    deleteSavedView,
  } = useSavedViewsContext();

  const handleSwitchView = (viewId: string) => {
    switchToView(viewId);
    setIsOpen(false);
  };

  const handleClearView = () => {
    clearAppliedView();
    setIsOpen(false);
  };

  const handleSaveNew = () => {
    setIsEditing(false);
    setIsModalOpen(true);
    setIsOpen(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setIsModalOpen(true);
    setIsOpen(false);
  };

  const handleUpdate = () => {
    if (!savedView) return;
    setIsSaving(true);
    updateSavedView(savedView.name).then(() => {
      setIsSaving(false);
      setIsOpen(false);
    });
  };

  const handleDeleteClick = (viewId: string) => {
    setDeleteTargetId(viewId);
    setIsDeleteConfirmOpen(true);
    setIsOpen(false);
  };

  const closeDeleteConfirm = () => {
    setIsDeleteConfirmOpen(false);
    setDeleteTargetId(null);
  };

  const handleConfirmDelete = () => {
    if (!deleteTargetId) return;
    setIsDeleting(true);
    deleteSavedView(deleteTargetId).then(() => {
      setIsDeleting(false);
      closeDeleteConfirm();
    });
  };

  const handleSaveView = (name: string) => {
    setIsSaving(true);
    const action = isEditing ? updateSavedView(name) : saveCurrentView(name);
    action.then(() => {
      setIsModalOpen(false);
      setIsSaving(false);
    });
  };

  const deleteTarget = savedViews.find((v) => v.id === deleteTargetId);
  const activeName = isApplied && savedView ? savedView.name : undefined;
  const iconClassName = iconOnly ? "h-4 w-4" : "h-4 w-4 mr-1";

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <Tooltip
          content={
            iconOnly && activeName ? `View: ${activeName}` : "Saved views"
          }
          placement="bottom"
        >
          <PopoverTrigger>
            <CourieButton
              size="xs"
              color={isApplied ? "success" : "secondary"}
              outline={!isApplied}
              aria-label="Saved views"
              onClick={() => setIsOpen((v) => !v)}
            >
              {isApplied ? (
                <BookmarkIconSolid className={iconClassName} />
              ) : (
                <BookmarkIcon className={iconClassName} />
              )}
              {!iconOnly && activeName ? (
                <span className="truncate max-w-[120px]">{activeName}</span>
              ) : (
                !iconOnly && <span></span>
              )}
            </CourieButton>
          </PopoverTrigger>
        </Tooltip>

        <PopoverContent className="absolute z-30 bg-white dark:bg-gray-800 border dark:border-gray-700 min-w-[260px] p-0 shadow-2xl rounded-md overflow-hidden">
          {/* Header */}
          <div className="px-3 pt-3 pb-2">
            <h3 className="font-semibold text-sm dark:text-gray-100">Views</h3>
          </div>

          {savedViewsLoading ? (
            <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : savedViews.length > 0 ? (
            <>
              {/* View list */}
              <div className="max-h-[240px] overflow-y-auto border-t dark:border-gray-700">
                {savedViews.map((view) => (
                  <ViewRow
                    key={view.id}
                    view={view}
                    isActive={view.id === savedView?.id}
                    onSwitch={() => handleSwitchView(view.id)}
                    onUpdate={view.id === savedView?.id ? handleUpdate : undefined}
                    onEdit={view.id === savedView?.id ? handleEdit : undefined}
                    onDelete={() => handleDeleteClick(view.id)}
                    isSaving={isSaving && view.id === savedView?.id}
                  />
                ))}
              </div>

              {/* Actions */}
              <div className="border-t dark:border-gray-700 p-2 flex flex-col gap-1">
                {isApplied && (
                  <CourieButton
                    size="xs"
                    color="secondary"
                    outline
                    className="w-full justify-center"
                    onClick={handleClearView}
                  >
                    View as default
                  </CourieButton>
                )}
                <CourieButton
                  size="xs"
                  color="primary"
                  className="w-full justify-center"
                  onClick={handleSaveNew}
                >
                  Save as new view
                </CourieButton>
              </div>
            </>
          ) : (
            <>
              {/* No saved views */}
              <div className="p-4 text-center">
                <BookmarkIcon className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  No saved view yet
                </p>
                <CourieButton
                  size="xs"
                  className="w-full justify-center"
                  onClick={handleSaveNew}
                >
                  Save current filters
                </CourieButton>
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>

      <SaveViewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveView}
        initialName={isEditing ? savedView?.name || "" : ""}
        isEditing={isEditing}
        isLoading={isSaving}
      />

      <WarningConfirmationModal
        isModalOpen={isDeleteConfirmOpen && !!deleteTarget}
        setIsModalOpen={(open) => !open && closeDeleteConfirm()}
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={closeDeleteConfirm}
        title="Delete Saved View"
        textBody={`Are you sure you want to delete "${deleteTarget?.name ?? ""}"? This action cannot be undone.`}
        primaryButtonText="Delete"
        primaryButtonColor="failure"
      />
    </>
  );
}

// ── ViewRow ──────────────────────────────────────────────────────────

interface ViewRowProps {
  view: SavedView;
  isActive: boolean;
  isSaving: boolean;
  onSwitch: () => void;
  onUpdate?: () => void;
  onEdit?: () => void;
  onDelete: () => void;
}

function ViewRow({
  view,
  isActive,
  isSaving,
  onSwitch,
  onUpdate,
  onEdit,
  onDelete,
}: ViewRowProps) {
  return (
    <div
      className={`flex items-center justify-between px-3 py-2 ${
        isActive
          ? "bg-primary-50 dark:bg-primary-900/20"
          : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
      }`}
    >
      {/* View name — click to switch */}
      <button
        onClick={onSwitch}
        className="flex-1 text-left min-w-0"
      >
        <p
          className={`text-sm truncate ${
            isActive
              ? "font-medium text-primary dark:text-primary-400"
              : "text-gray-900 dark:text-gray-100"
          }`}
        >
          {view.name}
        </p>
      </button>

      {/* Actions for active view */}
      {isActive && (
        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
          {onUpdate && (
            <Tooltip content="Update with current filters" placement="bottom">
              <CourieButton
                size="xs"
                color="secondary"
                outline
                onClick={onUpdate}
                disabled={isSaving}
              >
                <ArrowPathIcon
                  className={`h-3.5 w-3.5 ${isSaving ? "animate-spin" : ""}`}
                />
              </CourieButton>
            </Tooltip>
          )}
          {onEdit && (
            <Tooltip content="Edit name" placement="bottom">
              <CourieButton
                size="xs"
                color="secondary"
                outline
                onClick={onEdit}
              >
                <PencilIcon className="h-3.5 w-3.5" />
              </CourieButton>
            </Tooltip>
          )}
          <Tooltip content="Delete view" placement="bottom">
            <CourieButton
              size="xs"
              color="failure"
              outline
              onClick={onDelete}
            >
              <TrashIcon className="h-3.5 w-3.5" />
            </CourieButton>
          </Tooltip>
        </div>
      )}

      {/* Delete for non-active views */}
      {!isActive && (
        <div className="flex items-center ml-2 flex-shrink-0">
          <Tooltip content="Delete view" placement="bottom">
            <CourieButton
              size="xs"
              color="failure"
              outline
              onClick={onDelete}
            >
              <TrashIcon className="h-3.5 w-3.5" />
            </CourieButton>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

export default SavedViewsDropdown;
